/**
 * 統一的時間刻度工具函數
 * 確保所有圖表使用一致的時間刻度邏輯
 */
import { DateTime } from 'luxon';

// ─────────────────────────────────────────────────────────────────────────────
// 常數定義
const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

// 統一的時間刻度配置 - 優化以確保刻度間距一致
// 目標：在不同時間範圍下都保持約 8-12 個刻度，避免畫面太擠
// 使用陣列確保順序正確（從小到大）
const TIME_SCALE_CONFIGS = [
    // 6小時以內：每小時一個刻度 (最多 6 個)
    { maxSpan: 6 * HOUR_MS, unit: 'hour', stepHours: 1, mode: 'hourly' },
    // 12小時以內：每2小時一個刻度 (最多 6 個)
    { maxSpan: 12 * HOUR_MS, unit: 'hour', stepHours: 2, mode: 'bi-hourly' },
    // 1天以內：每3小時一個刻度 (最多 8 個)
    { maxSpan: DAY_MS, unit: 'hour', stepHours: 3, mode: 'tri-hourly' },
    // 3天以內：每6小時一個刻度 (最多 12 個)
    { maxSpan: 3 * DAY_MS, unit: 'hour', stepHours: 6, mode: 'six-hourly' },
    // 7天以內：每12小時一個刻度 (最多 14 個)
    { maxSpan: 7 * DAY_MS, unit: 'hour', stepHours: 12, mode: 'half-daily' },
    // 14天以內：每天一個刻度 (最多 14 個)
    { maxSpan: 14 * DAY_MS, unit: 'day', stepDays: 1, mode: 'daily' },
    // 32天以內（涵蓋完整月份）：每3天一個刻度 (約 10 個)
    { maxSpan: 32 * DAY_MS, unit: 'day', stepDays: 3, mode: 'tri-daily' },
    // 63天以內（約2個月）：每週一個刻度 (約 9 個)
    { maxSpan: 63 * DAY_MS, unit: 'day', stepDays: 7, mode: 'weekly' },
    // 120天以內（約4個月）：每兩週一個刻度 (約 8 個)
    { maxSpan: 120 * DAY_MS, unit: 'day', stepDays: 12, mode: 'bi-weekly' },
    // 240天以內（約8個月）：每月一個刻度 (約 8 個)
    { maxSpan: 240 * DAY_MS, unit: 'month', stepMonths: 1, mode: 'monthly' },
    // 366天以內（一年）：每月一個刻度 (最多 12 個)
    { maxSpan: 366 * DAY_MS, unit: 'month', stepMonths: 1, mode: 'monthly' },
    // 超過一年：每3月一個刻度
    { maxSpan: Infinity, unit: 'month', stepMonths: 3, mode: 'tri-monthly' },
];

// 預設配置（用於無效輸入）
const DEFAULT_CONFIG = TIME_SCALE_CONFIGS[0];

// 每個刻度標籤大約需要的最小寬度（像素）
const MIN_TICK_WIDTH = 50;

// ─────────────────────────────────────────────────────────────────────────────
// 根據時間跨度選擇適合的刻度配置（用於下載等固定寬度場景）
export function pickSpanMode(spanMs) {
    if (!Number.isFinite(spanMs) || spanMs <= 0) {
        return { ...DEFAULT_CONFIG };
    }

    const days = spanMs / DAY_MS;

    for (const config of TIME_SCALE_CONFIGS) {
        if (spanMs <= config.maxSpan) {
            console.log(
                `[pickSpanMode] spanMs=${spanMs}, days=${days.toFixed(1)}, selected mode=${config.mode}, stepDays=${config.stepDays}`
            );
            return { ...config };
        }
    }

    return { ...TIME_SCALE_CONFIGS[TIME_SCALE_CONFIGS.length - 1] };
}

// ─────────────────────────────────────────────────────────────────────────────
// 根據時間跨度和畫面寬度選擇適合的刻度配置（用於網頁顯示）
// chartWidth: 圖表的實際寬度（像素）
export function pickSpanModeForScreen(spanMs, chartWidth) {
    if (!Number.isFinite(spanMs) || spanMs <= 0) {
        return { ...DEFAULT_CONFIG };
    }

    // 計算螢幕可以容納的最大刻度數量
    const maxTicks = chartWidth ? Math.floor(chartWidth / MIN_TICK_WIDTH) : 12;
    const safeMaxTicks = Math.max(4, Math.min(maxTicks, 20)); // 限制在 4-20 之間

    // 從配置中找到適合的刻度設定
    // 優先考慮時間範圍，但如果刻度太多則使用更稀疏的配置
    for (let i = 0; i < TIME_SCALE_CONFIGS.length; i++) {
        const config = TIME_SCALE_CONFIGS[i];

        if (spanMs <= config.maxSpan) {
            // 計算這個配置會產生多少刻度
            let estimatedTicks;
            if (config.stepHours) {
                estimatedTicks = spanMs / (config.stepHours * HOUR_MS);
            } else if (config.stepDays) {
                estimatedTicks = spanMs / (config.stepDays * DAY_MS);
            } else if (config.stepMonths) {
                estimatedTicks = spanMs / (config.stepMonths * 30 * DAY_MS);
            } else {
                estimatedTicks = 10;
            }

            // 如果刻度數量在可接受範圍內，使用這個配置
            if (estimatedTicks <= safeMaxTicks) {
                return { ...config };
            }

            // 否則繼續尋找更稀疏的配置
            for (let j = i + 1; j < TIME_SCALE_CONFIGS.length; j++) {
                const sparserConfig = TIME_SCALE_CONFIGS[j];
                let sparserTicks;
                if (sparserConfig.stepHours) {
                    sparserTicks = spanMs / (sparserConfig.stepHours * HOUR_MS);
                } else if (sparserConfig.stepDays) {
                    sparserTicks = spanMs / (sparserConfig.stepDays * DAY_MS);
                } else if (sparserConfig.stepMonths) {
                    sparserTicks = spanMs / (sparserConfig.stepMonths * 30 * DAY_MS);
                } else {
                    sparserTicks = 10;
                }

                if (sparserTicks <= safeMaxTicks) {
                    return { ...sparserConfig };
                }
            }

            // 如果都不行，使用最稀疏的配置
            return { ...TIME_SCALE_CONFIGS[TIME_SCALE_CONFIGS.length - 1] };
        }
    }

    return { ...TIME_SCALE_CONFIGS[TIME_SCALE_CONFIGS.length - 1] };
}

// ─────────────────────────────────────────────────────────────────────────────
// 安全轉換為毫秒
export function toMsAny(v) {
    if (typeof v === 'number') return v;
    const n = Number(v);
    if (!Number.isNaN(n)) return n;
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d.getTime();
}

// ─────────────────────────────────────────────────────────────────────────────
// 建立刻度標籤格式化器
// 支援跨年時在該年第一個刻度下方顯示年份
export function createTickLabelFormatter(mode) {
    let lastDayKey = null;
    let lastMonthKey = null;
    let lastYearKey = null;
    let isFirstTick = true;
    let dataSpansMultipleYears = false;
    let firstYear = null;

    return (value, index, ticks) => {
        // 每次重新渲染時重置狀態
        if (index === 0) {
            lastDayKey = null;
            lastMonthKey = null;
            lastYearKey = null;
            isFirstTick = true;

            // 檢查資料是否跨年
            if (ticks && ticks.length >= 2) {
                const firstMs = toMsAny(ticks[0]?.value);
                const lastMs = toMsAny(ticks[ticks.length - 1]?.value);
                if (firstMs != null && lastMs != null) {
                    const firstDt = DateTime.fromMillis(firstMs).setZone('Asia/Taipei');
                    const lastDt = DateTime.fromMillis(lastMs).setZone('Asia/Taipei');
                    firstYear = firstDt.year;
                    dataSpansMultipleYears = firstDt.year !== lastDt.year;
                }
            }
        }

        const raw = ticks?.[index]?.value ?? value;
        const ms = toMsAny(raw);
        if (ms == null) return '';

        const dt = DateTime.fromMillis(ms).setZone('Asia/Taipei');
        if (!dt.isValid) return '';

        const dayKey = dt.toISODate();
        const monthKey = dt.toFormat('yyyy-MM');
        const yearKey = dt.year.toString();

        // 判斷是否為該年的第一個刻度（用於跨年顯示）
        const isFirstOfYear = yearKey !== lastYearKey;
        const shouldShowYear = dataSpansMultipleYears && isFirstOfYear;
        lastYearKey = yearKey;

        // 月刻度模式（monthly, tri-monthly）
        if (mode === 'monthly' || mode === 'tri-monthly') {
            const isFirstOfMonth = monthKey !== lastMonthKey;
            lastMonthKey = monthKey;

            if (shouldShowYear) {
                return [dt.toFormat('M月'), dt.toFormat('yyyy')];
            }
            if (isFirstOfMonth) {
                return dt.toFormat('M月');
            }
            return dt.toFormat('M/d');
        }

        // 日刻度模式（daily, tri-daily, weekly, bi-weekly）
        if (['daily', 'tri-daily', 'weekly', 'bi-weekly'].includes(mode)) {
            if (shouldShowYear) {
                return [dt.toFormat('M/d'), dt.toFormat('yyyy')];
            }
            return dt.toFormat('M/d');
        }

        // 小時刻度模式（hourly, bi-hourly, tri-hourly, six-hourly, half-daily）
        const isFirstOfDay = dayKey !== lastDayKey;
        lastDayKey = dayKey;

        const timeHHmm = dt.toFormat('H:mm');

        if (shouldShowYear) {
            // 跨年的第一個刻度：顯示時間、日期、年份
            return [timeHHmm, dt.toFormat('M/d'), dt.toFormat('yyyy')];
        }

        if (!isFirstOfDay) return timeHHmm;

        const dateMd = dt.toFormat('M/d');
        return [timeHHmm, dateMd];
    };
}

// ─────────────────────────────────────────────────────────────────────────────
// 固定網格刻度插件 - 確保刻度間距完全一致
// 使用 afterBuildTicks hook 來替換 Chart.js 自動生成的刻度
export function createFixedGridTicksPlugin(spanMode) {
    const { unit, stepHours, stepDays, stepMonths } = spanMode;

    return {
        id: 'fixedGridTicks',

        // 使用 beforeUpdate 來修改 scale 的 afterBuildTicks
        beforeUpdate(chart) {
            const scale = chart.scales?.x;
            if (!scale) return;

            // 設定 afterBuildTicks callback
            scale.options.afterBuildTicks = (axis) => {
                const min = axis.min;
                const max = axis.max;
                if (min == null || max == null) return;

                const zone = axis.options?.time?.zone || 'Asia/Taipei';
                const ticks = [];

                if (unit === 'hour' && stepHours) {
                    const startDt = DateTime.fromMillis(min).setZone(zone);
                    const alignedHour = Math.floor(startDt.hour / stepHours) * stepHours;
                    let cur = startDt.startOf('day').plus({ hours: alignedHour });

                    if (cur.toMillis() < min) {
                        cur = cur.plus({ hours: stepHours });
                    }

                    while (cur.toMillis() <= max) {
                        ticks.push({ value: cur.toMillis(), major: cur.hour === 0 });
                        cur = cur.plus({ hours: stepHours });
                    }
                } else if (unit === 'day' && stepDays) {
                    let cur = DateTime.fromMillis(min).setZone(zone).startOf('day');

                    if (cur.toMillis() < min) {
                        cur = cur.plus({ days: 1 });
                    }

                    console.log(
                        `[fixedGridTicks] unit=day, stepDays=${stepDays}, generating ticks...`
                    );

                    while (cur.toMillis() <= max) {
                        ticks.push({ value: cur.toMillis(), major: cur.day === 1 });
                        console.log(`[fixedGridTicks] tick: ${cur.toFormat('M/d')}`);
                        cur = cur.plus({ days: stepDays });
                    }
                } else if (unit === 'month' && stepMonths) {
                    let cur = DateTime.fromMillis(min).setZone(zone).startOf('month');

                    while (cur.toMillis() <= max) {
                        const ms = cur.toMillis();
                        if (ms >= min && ms <= max) {
                            ticks.push({ value: ms, major: cur.month === 1 });
                        }
                        cur = cur.plus({ months: stepMonths });
                    }
                }

                if (Number.isFinite(min) && Number.isFinite(max)) {
                    const nextTicks = ticks.slice();
                    const minMs = min;
                    const maxMs = max;
                    const epsilon = 1;

                    const first = nextTicks[0]?.value;
                    if (first == null || Math.abs(first - minMs) > epsilon) {
                        nextTicks.unshift({ value: minMs, major: false });
                    }

                    nextTicks.sort((a, b) => a.value - b.value);
                    const unique = [];
                    let lastValue = null;
                    for (const tick of nextTicks) {
                        if (lastValue == null || Math.abs(tick.value - lastValue) > epsilon) {
                            unique.push(tick);
                            lastValue = tick.value;
                        }
                    }

                    if (unique.length > 0) {
                        axis.ticks = unique;
                    }
                } else if (ticks.length > 0) {
                    axis.ticks = ticks;
                }
            };
        },
    };
}

// ─────────────────────────────────────────────────────────────────────────────
// 建立 Chart.js 時間軸配置
export function createTimeScaleConfig(spanMode) {
    const { unit, stepHours, stepDays, stepMonths } = spanMode;

    const config = {
        zone: 'Asia/Taipei',
        unit,
    };

    if (unit === 'hour' && stepHours) {
        config.stepSize = stepHours;
    } else if (unit === 'day' && stepDays) {
        config.stepSize = stepDays;
    } else if (unit === 'month' && stepMonths) {
        config.stepSize = stepMonths;
    }

    return config;
}

// ─────────────────────────────────────────────────────────────────────────────
// Y 軸漂亮步階（保持原有邏輯）
export function niceStep(min, max, maxTicks = 6) {
    const range = Math.abs(max - min);
    if (range === 0) return 1;
    const rough = range / Math.max(2, maxTicks);
    const pow10 = Math.pow(10, Math.floor(Math.log10(rough)));
    const base = rough / pow10;
    const niceBase = base <= 1 ? 1 : base <= 2 ? 2 : base <= 5 ? 5 : 10;
    return niceBase * pow10;
}
